export const DEFAULT_EVENT = 'ready';
export const CUSTOM_EVENT_VALUE = '__custom__';

export const METHOD_EVENTS = {
  kiosk: [
    { key: 'preparing', label: 'Preparing your prescription' },
    { key: 'ready', label: 'Ready to pick up' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'expiry_warning', label: 'Expiry warning' },
    { key: 'future_release', label: 'Future release now available' },
    { key: 'consult_required', label: 'Consultation required' },
    { key: 'on_hold', label: 'On hold' },
    { key: 'hold_released', label: 'Hold released / available again' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'removed', label: 'Removed from locker' },
    { key: 'forgot_code', label: 'Forgot pickup code' },
    { key: 'no_space', label: 'Locker full / pick up at pharmacy' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  counter: [
    { key: 'ready', label: 'Ready to pick up' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'consult_required', label: 'Consultation required' },
    { key: 'out_for_delivery', label: 'Out for delivery' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'cancelled', label: 'Cancelled order' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'forgot_code', label: 'Forgot pickup code' },
    { key: 'no_space', label: 'Locker full / pick up at pharmacy' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  curbside: [
    { key: 'preparing', label: 'Preparing your prescription' },
    { key: 'ready', label: 'Ready to pick up' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'out_for_delivery', label: 'Out for delivery' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  bedside: [
    { key: 'assignment', label: 'Assigned / ready at bedside' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'out_for_delivery', label: 'Out for delivery' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  courier: [
    { key: 'ready', label: 'Ready to pick up' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'out_for_delivery', label: 'Out for delivery' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  mail_order: [
    { key: 'ready', label: 'Ready to pick up' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'out_for_delivery', label: 'Out for delivery' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  other: [
    { key: 'ready', label: 'Ready to pick up' },
    { key: 'reminder', label: 'Pickup reminder' },
    { key: 'out_for_delivery', label: 'Out for delivery' },
    { key: 'picked_up', label: 'Items are picked up' },
    { key: 'receipt', label: 'Payment receipt' },
    { key: 'sms_enrollment', label: 'Enroll in text notifications' },
  ],
  account: [
    { key: 'welcome', label: 'Staff welcome / temp credentials' },
    { key: 'password_reset', label: 'Password reset' },
    { key: 'otp', label: 'One-time passcode' },
    { key: 'kiosk_offline', label: 'Kiosk offline / online' },
  ],
};

export const slugifyEvent = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

export const isValidEventKey = (value) => {
  const key = slugifyEvent(value);
  return !!key && key !== CUSTOM_EVENT_VALUE && /^[a-z][a-z0-9_]{1,63}$/.test(key);
};

const titleCaseEvent = (value) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const eventsForMethod = (method, channel) => {
  const list = METHOD_EVENTS[method] || [{ key: DEFAULT_EVENT, label: 'Ready to pick up' }];
  if (channel === 'sms') {
    return list.filter(
      (event) =>
        event.key !== 'no_space' &&
        event.key !== 'welcome' &&
        event.key !== 'password_reset' &&
        event.key !== 'kiosk_offline'
    );
  }
  if (channel === 'mobile') {
    if (method === 'account') return [];
    const key = method === 'bedside' ? 'assignment' : 'ready';
    return list.filter((event) => event.key === key);
  }
  return list;
};

export const eventLabel = (eventKey, method) => {
  const match = eventsForMethod(method).find((event) => event.key === eventKey);
  return (match && match.label) || titleCaseEvent(eventKey) || 'Ready to pick up';
};

const catalogLabel = (eventKey) => {
  const methods = Object.keys(METHOD_EVENTS);
  for (let i = 0; i < methods.length; i += 1) {
    const match = METHOD_EVENTS[methods[i]].find((event) => event.key === eventKey);
    if (match) return match.label;
  }
  return titleCaseEvent(eventKey);
};

/**
 * When each Studio template is actually sent. Labels stay in METHOD_EVENTS.
 * `when` is the sentence shown on the event card and in the ? help menu.
 */
export const EVENT_WHEN = {
  preparing: {
    when: 'Sent when the order is assigned but not ready yet. The pharmacy is still preparing it, so there is no pickup code.',
    methods: 'Kiosk, Curbside',
    channels: 'SMS and email',
  },
  ready: {
    when: 'Sent when the order is stocked and ready to pick up. Includes the pickup code and the link to the order page. Bedside uses Assigned / ready at bedside instead.',
    methods: 'Kiosk, Counter, Curbside, Courier, Mail order, Other',
    channels: 'SMS, email, and the patient link page',
  },
  reminder: {
    when: 'Sent on the reminder schedule while the order is still waiting to be picked up. Includes the pickup code and link.',
    methods: 'Every location type',
    channels: 'SMS and email',
  },
  expiry_warning: {
    when: 'Sent before a locker order expires, telling the patient how long they have left to pick it up.',
    methods: 'Kiosk',
    channels: 'SMS and email',
  },
  future_release: {
    when: 'Sent instead of Ready when a future-release order is stocked and becomes available for pickup.',
    methods: 'Kiosk',
    channels: 'SMS and email',
  },
  consult_required: {
    when: 'Sent when the patient must speak with the pharmacy before they can pick up. Asks them to call.',
    methods: 'Kiosk, Counter',
    channels: 'SMS and email',
  },
  on_hold: {
    when: 'Sent when staff place the prescription on hold. Asks the patient to contact the pharmacy. There is no pickup link.',
    methods: 'Kiosk',
    channels: 'SMS and email',
  },
  hold_released: {
    when: 'Sent when staff release a hold and the order is available again. Includes the pickup code.',
    methods: 'Kiosk',
    channels: 'SMS and email',
  },
  picked_up: {
    when: 'Sent after pickup is complete, confirming the items were collected.',
    methods: 'Every location type',
    channels: 'SMS and email',
  },
  cancelled: {
    when: 'Sent when staff cancel a counter order.',
    methods: 'Counter',
    channels: 'SMS and email',
  },
  receipt: {
    when: 'Sent after a payment settles, with the transaction details.',
    methods: 'Every location type',
    channels: 'SMS and email',
  },
  removed: {
    when: 'Sent when the prescription is taken out of the locker and returned to the pharmacy. Asks the patient to call.',
    methods: 'Kiosk',
    channels: 'SMS and email',
  },
  forgot_code: {
    when: 'Sent when the patient asks for their pickup code again.',
    methods: 'Kiosk, Counter',
    channels: 'SMS and email',
  },
  assignment: {
    when: 'Sent when a bedside order is assigned and ready. This is the bedside version of Ready, with the pickup code and link.',
    methods: 'Bedside',
    channels: 'SMS, email, and the patient link page',
  },
  out_for_delivery: {
    when: 'Sent when the order leaves for delivery and is on the way.',
    methods: 'Counter, Curbside, Bedside, Courier, Mail order, Other',
    channels: 'SMS and email',
  },
  no_space: {
    when: 'Sent when there is no locker space and the patient should pick the order up at the pharmacy instead.',
    methods: 'Kiosk, Counter',
    channels: 'Email only',
  },
  sms_enrollment: {
    when: 'Sent when the pharmacy invites the patient to sign up for text notifications. Includes the enrollment link.',
    methods: 'Every location type',
    channels: 'SMS and email',
  },
  welcome: {
    when: 'Sent when a new staff user is created, with a temporary password.',
    methods: 'Staff',
    channels: 'Email only',
  },
  password_reset: {
    when: 'Sent when a staff user asks to reset their password.',
    methods: 'Staff',
    channels: 'Email only',
  },
  otp: {
    when: 'Sent with a one-time passcode when a staff user signs in.',
    methods: 'Staff',
    channels: 'Email',
  },
  kiosk_offline: {
    when: 'Sent to staff when a kiosk goes offline or comes back online.',
    methods: 'Staff',
    channels: 'Email only',
  },
};

const HELP_ORDER = [
  'preparing',
  'ready',
  'assignment',
  'reminder',
  'expiry_warning',
  'future_release',
  'consult_required',
  'on_hold',
  'hold_released',
  'out_for_delivery',
  'picked_up',
  'cancelled',
  'receipt',
  'removed',
  'forgot_code',
  'no_space',
  'sms_enrollment',
  'welcome',
  'password_reset',
  'otp',
  'kiosk_offline',
];

const STAFF_KEYS = ['welcome', 'password_reset', 'otp', 'kiosk_offline'];

export const eventWhen = (eventKey) =>
  (EVENT_WHEN[eventKey] && EVENT_WHEN[eventKey].when) || '';

const uniqueSorted = (keys) => {
  const seen = {};
  return keys
    .filter((key) => {
      if (!key || seen[key] || !EVENT_WHEN[key]) return false;
      seen[key] = true;
      return true;
    })
    .sort((a, b) => {
      const ai = HELP_ORDER.indexOf(a);
      const bi = HELP_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
};

const toHelpEntry = (key) => ({
  key,
  label: catalogLabel(key),
  when: EVENT_WHEN[key].when,
  methods: EVENT_WHEN[key].methods,
  channels: EVENT_WHEN[key].channels,
  staff: STAFF_KEYS.indexOf(key) !== -1,
});

/**
 * Entries for the Studio “when is this message sent” menu.
 * Pass method and/or channel to limit the list to that page.
 */
export const messageHelpGroups = ({ method, channel } = {}) => {
  const staffOnly = channel === 'staff' || method === 'account';
  const patientMethods = Object.keys(METHOD_EVENTS).filter(
    (item) => item !== 'account'
  );
  let patientKeys = [];
  if (!staffOnly) {
    if (method && METHOD_EVENTS[method]) {
      patientKeys = eventsForMethod(
        method,
        channel === 'notes' ? undefined : channel
      ).map((event) => event.key);
    } else if (channel === 'mobile') {
      patientKeys = ['ready', 'assignment'];
    } else if (channel === 'sms') {
      patientKeys = patientMethods.reduce(
        (keys, item) =>
          keys.concat(eventsForMethod(item, 'sms').map((event) => event.key)),
        []
      );
    } else {
      patientKeys = patientMethods.reduce(
        (keys, item) => keys.concat(METHOD_EVENTS[item].map((event) => event.key)),
        []
      );
    }
  }
  const includeStaff = staffOnly || (!method && !channel);
  const staffKeys = includeStaff ? METHOD_EVENTS.account.map((event) => event.key) : [];
  return {
    patient: uniqueSorted(patientKeys).map(toHelpEntry),
    staff: uniqueSorted(staffKeys).map(toHelpEntry),
  };
};



// WEBPACK FOOTER //
// ./src/components/message-control-center/events.js